import { useQueries, useQueryClient } from '@tanstack/react-query';
import { loadFailureMessage } from '@evogard/domain';
import type { RetentionSummary } from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/** A raiz das chaves do Kanban Cancelamento — toda mudança invalida tudo daqui para baixo. */
export const RETENTION_KEY = ['retencao', 'kanban'] as const;

export interface RetentionTenant {
  id: number;
  slug: string;
  name: string;
}

export interface RetentionFilters {
  q?: string;
  from?: string;
  to?: string;
}

/** As bases da sessão — o quadro junta CE e GO, como a Mesa da cobrança. */
export function useRetentionTenants(): RetentionTenant[] {
  const { data: context } = useSessionContext();
  return (context?.tenants ?? []).map((t) => ({ id: t.id, slug: t.slug, name: t.nome }));
}

/**
 * O resumo de cada base. A base que recusa (403: sem `retention.requests:ver`
 * nela) fica fora do quadro, e a tela diz qual.
 */
export function useRetentionSummaries(filters: RetentionFilters) {
  const tenants = useRetentionTenants();
  const results = useQueries({
    queries: tenants.map((tenant) => ({
      queryKey: [...RETENTION_KEY, 'resumo', tenant.slug, filters],
      queryFn: async () => {
        const r = await api.retention.summary({
          params: { tenant: tenant.slug },
          query: filters,
        });
        if (r.status === 403 || r.status === 404) return null;
        if (r.status !== 200) {
          throw new Error(
            loadFailureMessage(r.status, `o Kanban Cancelamento da ${tenant.name}`),
          );
        }
        return r.body;
      },
      refetchInterval: 120_000,
    })),
  });

  const ready: Array<{ tenant: RetentionTenant; summary: RetentionSummary }> = [];
  const refused: RetentionTenant[] = [];
  const errors: string[] = [];
  results.forEach((r, i) => {
    const tenant = tenants[i]!;
    if (r.error) errors.push(r.error.message);
    else if (r.data === null) refused.push(tenant);
    else if (r.data) ready.push({ tenant, summary: r.data });
  });

  return { tenants, ready, refused, errors, loading: results.some((r) => r.isLoading) };
}

export function useInvalidateRetention() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: RETENTION_KEY });
}
